import { Component, Input, OnInit } from '@angular/core';
import { AppState } from '@core/constants/app-state.enum';
import {
  VEHICLE_CONDITION_LABELS,
  VEHICLE_STATUS_LABELS,
  VehicleCondition,
  VehicleDetails,
  VehicleStatus,
} from '@core/models';
import { TranslateService } from '@ngx-translate/core';
import { getAngularService } from '../../../ng-upgrade.utils';
import { DialogService } from '../../../services/dialog.service';
import { NavigationService } from '../../../services/navigation.service';
import { ToastService } from '../../../services/toast.service';
import { VehicleChangesHistoryService } from '../../../services/vehicle-changes-history.service';

const EMPTY_VALUE_FALLBACK = 'N/A';

interface DetailField {
  key: keyof VehicleDetails;
  labelKey: string;
  format?: 'plain' | 'mileage' | 'currency' | 'yearRange' | 'condition';
}

const IDENTIFICATION_FIELDS: ReadonlyArray<DetailField> = [
  { key: 'plate', labelKey: 'VEHICLE_ACTIONS.FORM.STEP_TWO.INPUT_PLATE' },
  { key: 'vin', labelKey: 'VEHICLE_HISTORIC.DETAILS.VIN' },
  {
    key: 'manufacturingYear',
    labelKey: 'VEHICLE_HISTORIC.DETAILS.MANUFACTURING_MODEL_YEAR',
    format: 'yearRange',
  },
  {
    key: 'conditionType',
    labelKey: 'VEHICLE_ACTIONS.FORM.STEP_TWO.INPUT_CONDITION_TYPE',
    format: 'condition',
  },
  { key: 'id', labelKey: 'VEHICLE_HISTORIC.DETAILS.INTERNAL_ID' },
];

const SPECIFICATION_FIELDS: ReadonlyArray<DetailField> = [
  { key: 'colorName', labelKey: 'VEHICLE_ACTIONS.FORM.STEP_TWO.INPUT_COLOR' },
  { key: 'fuelName', labelKey: 'VEHICLE_ACTIONS.FORM.STEP_TWO.INPUT_FUEL' },
  { key: 'transmissionName', labelKey: 'VEHICLE_ACTIONS.FORM.STEP_TWO.INPUT_GEARBOX' },
  { key: 'doors', labelKey: 'VEHICLE_ACTIONS.FORM.STEP_TWO.INPUT_DOORS_AMOUNT' },
  { key: 'kilometers', labelKey: 'VEHICLE_HISTORIC.DETAILS.MILEAGE', format: 'mileage' },
  { key: 'value', labelKey: 'VEHICLE_ACTIONS.FORM.STEP_SIX.INPUT_VALUE', format: 'currency' },
];

const TIMESTAMPS: ReadonlyArray<{ key: keyof VehicleDetails; labelKey: string }> = [
  { key: 'acquisitionAt', labelKey: 'VEHICLE_HISTORIC.DETAILS.ACQUISITION' },
  { key: 'createdAt', labelKey: 'VEHICLE_HISTORIC.DETAILS.CREATED_AT' },
  { key: 'updatedAt', labelKey: 'VEHICLE_HISTORIC.DETAILS.UPDATED_AT' },
];

@Component({
  selector: 'app-vehicle-history-details-dialog',
  styles: [
    `
      :host {
        display: flex;
        flex: 1 1 auto;
        flex-direction: column;
        min-height: 0;
        width: 560px;
        max-width: 92vw;
      }
    `,
  ],
  template: `
    <aav-dialog
      data-dialog="vehicle-history-details"
      [heading]="'VEHICLE_HISTORIC.DETAILS.TITLE' | translate"
      [subheading]="(changeDate | dateFormat: 'dd/MM/yyyy') + ' · ' + (entityName || notAvailable)"
      [closeLabel]="'GENERIC.ACTIONS_CLOSE' | translate"
      (dismissed)="close()"
    >
      <div *ngIf="loading" class="relative min-h-[8.5rem]">
        <app-loading-spinner
          [visible]="true"
          [ariaLabel]="'VEHICLE_HISTORIC.LOADING' | translate"
        ></app-loading-spinner>
      </div>

      <p
        *ngIf="!loading && loadFailed"
        data-state="error"
        class="m-0 px-[1.5rem] py-[3rem] text-center text-sm text-aav-on-surface-variant"
      >
        {{ 'VEHICLE_HISTORIC.ERROR.LOAD_DETAILS' | translate }}
      </p>

      <div *ngIf="!loading && !loadFailed" class="px-[1.5rem] py-[1.25rem]">
        <!-- Identidade do veículo -->
        <div class="flex items-center gap-2">
          <h3
            class="m-0 text-[17px] font-bold uppercase leading-tight text-aav-on-surface"
            data-info="vehicle-name"
          >
            {{ vehicleName }}
          </h3>
          <span
            class="rounded-full border-[1px] border-solid border-aav-outline-variant bg-aav-surface-container-lowest px-[0.625rem] py-[0.125rem] text-[11px] font-bold text-aav-on-surface-variant"
            data-info="status"
          >
            {{ statusLabelKey | translate }}
          </span>
        </div>
        <p
          *ngIf="displayVersionName"
          class="mb-0 mt-[2px] text-[13px] text-aav-on-surface-variant"
          data-info="version"
        >
          {{ displayVersionName }}
        </p>

        <!-- Identificação -->
        <h4
          class="mt-[1.125rem] mb-[0.375rem] text-aav-label-small-strong uppercase tracking-wide text-aav-slate-400"
        >
          {{ 'VEHICLE_HISTORIC.DETAILS.SECTION_IDENTIFICATION' | translate }}
        </h4>
        <dl class="m-0 grid grid-cols-3 gap-x-4 gap-y-[0.75rem]">
          <div *ngFor="let field of identificationFields" [attr.data-field]="field.key">
            <dt class="m-0 text-[11px] font-normal leading-tight text-aav-slate-400">
              {{ field.labelKey | translate }}
            </dt>
            <dd class="m-0 text-[13px] font-semibold leading-snug text-aav-on-surface">
              {{ display(field) }}
            </dd>
          </div>
        </dl>

        <!-- Especificações -->
        <h4
          class="mt-[1.125rem] mb-[0.375rem] text-aav-label-small-strong uppercase tracking-wide text-aav-slate-400"
        >
          {{ 'VEHICLE_HISTORIC.DETAILS.SECTION_SPECIFICATIONS' | translate }}
        </h4>
        <dl class="m-0 grid grid-cols-3 gap-x-4 gap-y-[0.75rem]">
          <div *ngFor="let field of specificationFields" [attr.data-field]="field.key">
            <dt class="m-0 text-[11px] font-normal leading-tight text-aav-slate-400">
              {{ field.labelKey | translate }}
            </dt>
            <dd class="m-0 text-[13px] font-semibold leading-snug text-aav-on-surface">
              {{ display(field) }}
            </dd>
          </div>
        </dl>

        <!-- Alteração: 100% vinda da LINHA do histórico, nada de mock -->
        <h4
          class="mt-[1.125rem] mb-[0.375rem] text-aav-label-small-strong uppercase tracking-wide text-aav-slate-400"
        >
          {{ 'VEHICLE_HISTORIC.DETAILS.SECTION_CHANGE' | translate }}
        </h4>
        <div class="rounded border-[1px] border-solid border-aav-neutral-200">
          <div
            class="flex items-center justify-between border-b border-solid border-t-0 border-l-0 border-r-0 border-aav-neutral-200 bg-aav-surface-container-lowest px-[1rem] py-[0.5rem]"
          >
            <span class="text-[13px] font-bold text-aav-on-surface">
              {{ 'VEHICLE_HISTORIC.DETAILS.CHANGE_TYPE' | translate }}
            </span>
            <span class="text-[13px] font-semibold text-aav-on-surface" data-info="change-type">
              {{ changeType | historicTypeKey | translate }}
            </span>
          </div>
          <!-- Tres celulas: valor anterior | seta | novo valor. A seta fica numa celula
                 propria com borda dos dois lados, e o items-stretch faz os divisores irem
                 de topo a base da linha em vez de acompanharem so a altura do icone. -->
          <div class="flex items-stretch">
            <div class="flex-1 px-[1rem] py-[0.75rem]">
              <dt class="m-0 text-[11px] font-normal text-aav-slate-400">
                {{ 'VEHICLE_HISTORIC.DETAILS.PREVIOUS_VALUE' | translate }}
              </dt>
              <dd class="m-0 text-[13px] font-semibold text-aav-on-surface" data-info="old-value">
                {{ oldValue || notAvailable }}
              </dd>
            </div>
            <div
              data-info="change-arrow"
              class="flex flex-none items-center justify-center border-b-0 border-l border-r border-t-0 border-solid border-aav-neutral-200 px-[0.75rem]"
            >
              <aav-icon name="arrow_forward" [size]="16" class="text-aav-slate-400"></aav-icon>
            </div>
            <div class="flex-1 px-[1rem] py-[0.75rem]">
              <dt class="m-0 text-[11px] font-normal text-aav-slate-400">
                {{ 'VEHICLE_HISTORIC.COLUMNS_TABLE.NEW_VALUE' | translate }}
              </dt>
              <dd class="m-0 text-[13px] font-semibold text-aav-on-surface" data-info="new-value">
                {{ newValue || notAvailable }}
              </dd>
            </div>
          </div>
        </div>

        <!-- Carimbos de tempo -->
        <p
          class="mb-0 mt-[0.75rem] text-[11px] leading-relaxed text-aav-slate-400"
          data-info="timestamps"
        >
          <span *ngFor="let stamp of timestamps; let last = last" [attr.data-stamp]="stamp.key">
            <span>
              {{ stamp.labelKey | translate }}
              {{ (stampValue(stamp.key) | dateFormat: 'dd/MM/yyyy HH:mm') || notAvailable }}
            </span>
            <span *ngIf="!last"> · </span>
          </span>
        </p>
      </div>

      <ng-container aavDialogActions>
        <aav-button
          variant="outlined-neutral"
          data-action="view-vehicle"
          class="uppercase"
          (clicked)="viewVehicle()"
        >
          {{ 'VEHICLE_HISTORIC.DETAILS.VIEW_VEHICLE' | translate }}
        </aav-button>
        <aav-button variant="filled" data-action="ok" class="uppercase" (clicked)="close()">
          {{ 'GENERIC.ACTIONS_CLOSE' | translate }}
        </aav-button>
      </ng-container>
    </aav-dialog>
  `,
})
export class VehicleHistoryDetailsDialogComponent implements OnInit {
  @Input() vehicleId?: number;
  @Input() changeDate?: string;
  @Input() entityName?: string;
  @Input() changeType?: number | string;
  @Input() oldValue?: string;
  @Input() newValue?: string;
  @Input() versionName?: string;

  readonly identificationFields = IDENTIFICATION_FIELDS;
  readonly specificationFields = SPECIFICATION_FIELDS;
  readonly timestamps = TIMESTAMPS;

  vehicle: VehicleDetails | null = null;
  loading = false;
  loadFailed = false;

  private historyService: VehicleChangesHistoryService | null = null;
  private dialogService: DialogService | null = null;
  private navigationService: NavigationService | null = null;
  private toastService: ToastService | null = null;
  private translateService: TranslateService | null = null;

  ngOnInit(): void {
    this.historyService = getAngularService<VehicleChangesHistoryService>(
      VehicleChangesHistoryService
    );
    this.dialogService = getAngularService<DialogService>(DialogService);
    this.navigationService = getAngularService<NavigationService>(NavigationService);
    this.toastService = getAngularService<ToastService>(ToastService);
    this.translateService = getAngularService<TranslateService>(TranslateService);

    this.loadVehicle();
  }

  async loadVehicle(): Promise<void> {
    if (!this.historyService || this.vehicleId === undefined || this.vehicleId === null) {
      this.failWithoutPartialData();
      return;
    }

    this.loading = true;
    this.loadFailed = false;

    try {
      this.vehicle = await this.historyService.getVehicleById(this.vehicleId);
    } catch {
      this.failWithoutPartialData();
    } finally {
      this.loading = false;
    }
  }

  get vehicleName(): string {
    const name = [this.vehicle?.brandName, this.vehicle?.modelName].filter(Boolean).join(' ');
    return name || this.notAvailable;
  }

  get notAvailable(): string {
    return this.translateService?.instant('GENERIC.NOT_AVAILABLE') ?? EMPTY_VALUE_FALLBACK;
  }

  get displayVersionName(): string {
    return this.vehicle?.versionName || this.versionName || '';
  }

  get statusLabelKey(): string {
    const status = this.vehicle?.status;
    if (this.isBlank(status)) {
      return 'GENERIC.NOT_IDENTIFIED';
    }
    return VEHICLE_STATUS_LABELS[String(status) as VehicleStatus] ?? 'GENERIC.NOT_IDENTIFIED';
  }

  display(field: DetailField): string {
    const raw = this.vehicle?.[field.key];
    if (this.isBlank(raw)) {
      return this.notAvailable;
    }

    switch (field.format) {
      case 'mileage':
        return `${this.decimal(Number(raw), 0)} km`;
      case 'currency':
        return `${this.instant('GENERIC.CURRENCY_SYMBOL')}${this.decimal(Number(raw), 2)}`;
      case 'yearRange':
        return this.vehicle?.modelYear ? `${raw}/${this.vehicle.modelYear}` : String(raw);
      case 'condition': {
        const key = VEHICLE_CONDITION_LABELS[Number(raw) as VehicleCondition];
        return key ? this.instant(key) : this.notAvailable;
      }
      default:
        return String(raw);
    }
  }

  stampValue(key: keyof VehicleDetails): string {
    const raw = this.vehicle?.[key];
    return this.isBlank(raw) ? '' : String(raw);
  }

  viewVehicle(): void {
    const plate = this.vehicle?.plate;
    this.navigationService?.goToState(
      AppState.PanelVehicleList,
      plate ? { search: plate } : undefined
    );
    this.close();
  }

  close(): void {
    this.dialogService?.close();
  }

  private isBlank(value: unknown): boolean {
    return value === undefined || value === null || value === '';
  }

  private decimal(value: number, fractionDigits: number): string {
    return value.toLocaleString('pt-BR', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  }

  private failWithoutPartialData(): void {
    this.vehicle = null;
    this.loadFailed = true;
    this.toastService?.error(this.instant('VEHICLE_HISTORIC.ERROR.LOAD_DETAILS'));
  }

  private instant(key: string): string {
    return this.translateService?.instant(key) ?? key;
  }
}
